// Page "Fournisseurs" : camembert dépenses par fournisseur + liste des factures supplier.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { invoicesApi } from "../api/invoices";
import { clientsApi } from "../api/clients";
import type { ClientPublic, InvoiceListItem } from "../api/types";
import { AppShell } from "../components/AppShell";

// Palette couleurs (tons chauds/rouges pour différencier des clients)
const COLORS = [
  "#ef4444","#f97316","#f59e0b","#8b5cf6","#ec4899",
  "#06b6d4","#10b981","#3b82f6","#84cc16","#6366f1",
];

function fmt(n: number) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);
}

type Slice = { name: string; value: number; color: string; pct: number };

function PieChart({ slices, hovered, onHover }: {
  slices: Slice[];
  hovered: number | null;
  onHover: (i: number | null) => void;
}) {
  const cx = 110, cy = 110, r = 90;
  let angle = -Math.PI / 2;
  const paths = slices.map((s, i) => {
    const sweep = (s.pct / 100) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(angle);
    const y1 = cy + r * Math.sin(angle);
    angle += sweep;
    const x2 = cx + r * Math.cos(angle);
    const y2 = cy + r * Math.sin(angle);
    const large = sweep > Math.PI ? 1 : 0;
    const scale = hovered === i ? 1.06 : 1;
    return {
      d: `M${cx},${cy} L${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`,
      color: s.color, scale, i,
    };
  });
  return (
    <svg viewBox="0 0 220 220" className="w-56 h-56 drop-shadow-sm">
      {paths.map(p => (
        <path
          key={p.i} d={p.d} fill={p.color}
          stroke="white" strokeWidth="2"
          style={{ transform: `scale(${p.scale})`, transformOrigin: `${cx}px ${cy}px`, transition: "transform 0.15s" }}
          onMouseEnter={() => onHover(p.i)}
          onMouseLeave={() => onHover(null)}
          className="cursor-pointer"
        />
      ))}
      <circle cx={cx} cy={cy} r={42} fill="white" />
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill="#64748b">Total</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1e293b">
        {fmt(slices.reduce((s, sl) => s + sl.value, 0))}
      </text>
    </svg>
  );
}

export function SuppliersPage() {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [clients, setClients] = useState<ClientPublic[]>([]);
  const [q, setQ] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [hovered, setHovered] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [inv, cli] = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        invoicesApi.list({ limit: 200, invoice_type: "supplier" } as any),
        clientsApi.list({}),
      ]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items: InvoiceListItem[] = (inv as any).items ?? inv;
      setInvoices(items);
      setClients(cli);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, []); // eslint-disable-line

  const years = useMemo(() => {
    const s = new Set<string>();
    for (const inv of invoices) s.add((inv.issue_date as string).slice(0, 4));
    return Array.from(s).sort().reverse();
  }, [invoices]);

  const months = useMemo(() => {
    const s = new Set<string>();
    for (const inv of invoices) {
      const m = (inv.issue_date as string).slice(0, 7);
      if (!filterYear || m.startsWith(filterYear)) s.add(m);
    }
    return Array.from(s).sort().reverse();
  }, [invoices, filterYear]);

  const slices: Slice[] = useMemo(() => {
    const map = new Map<string, number>();
    for (const inv of invoices) {
      const d = inv.issue_date as string;
      if (filterYear && !d.startsWith(filterYear)) continue;
      if (filterMonth && !d.startsWith(filterMonth)) continue;
      const name = clients.find(c => c.id === inv.client_id)?.name ?? "Autre";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ttc = parseFloat((inv.amount_ttc as any)) || 0;
      map.set(name, (map.get(name) ?? 0) + ttc);
    }
    const total = Array.from(map.values()).reduce((s, v) => s + v, 0);
    if (total === 0) return [];
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({
        name, value, color: COLORS[i % COLORS.length],
        pct: (value / total) * 100,
      }));
  }, [invoices, clients, filterYear, filterMonth]);

  // Filtrage liste par recherche texte
  const filtered = useMemo(() => {
    if (!q.trim()) return invoices;
    const lq = q.toLowerCase();
    return invoices.filter(inv =>
      (inv.reference as string).toLowerCase().includes(lq) ||
      (clients.find(c => c.id === inv.client_id)?.name ?? "").toLowerCase().includes(lq)
    );
  }, [invoices, clients, q]);

  const activeSlice = hovered !== null ? slices[hovered] : null;

  // Statut badge
  function statusBadge(s: string) {
    const map: Record<string, string> = {
      issued: "bg-blue-100 text-blue-700",
      draft: "bg-amber-100 text-amber-700",
      paid: "bg-green-100 text-green-700",
      overdue: "bg-red-100 text-red-700",
      cancelled: "bg-slate-100 text-slate-500",
      partial: "bg-purple-100 text-purple-700",
    };
    return map[s] ?? "bg-slate-100 text-slate-500";
  }

  return (
    <AppShell>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">📤 {t("suppliersPage.title")}</h1>
          <p className="text-sm text-slate-500">{t("suppliersPage.subtitle")}</p>
        </div>
      </header>

      {/* CAMEMBERT */}
      {slices.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <h2 className="text-base font-semibold text-slate-800 mr-auto">
              {t("suppliersPage.chartTitle")}
            </h2>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">{t("common.year")}</label>
              <select value={filterYear} onChange={e => { setFilterYear(e.target.value); setFilterMonth(""); }}
                className="input text-sm py-1 min-w-[100px]">
                <option value="">{t("common.allYears")}</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">{t("common.month")}</label>
              <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
                className="input text-sm py-1 min-w-[130px]">
                <option value="">{t("common.allMonths")}</option>
                {months.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {(filterYear || filterMonth) && (
              <button type="button" className="text-xs text-slate-500 hover:text-slate-800 underline"
                onClick={() => { setFilterYear(""); setFilterMonth(""); }}>
                {t("common.reset")}
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-8">
            <div className="relative flex-shrink-0">
              <PieChart slices={slices} hovered={hovered} onHover={setHovered} />
              {activeSlice && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-white/95 rounded-lg px-3 py-2 shadow-lg text-center border border-slate-100 mt-10">
                    <p className="text-xs text-slate-500 truncate max-w-[130px]">{activeSlice.name}</p>
                    <p className="text-sm font-bold text-slate-900">{fmt(activeSlice.value)}</p>
                    <p className="text-xs text-slate-400">{activeSlice.pct.toFixed(1)} %</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
              {slices.map((s, i) => (
                <div key={s.name}
                  className={["flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer transition-colors",
                    hovered === i ? "bg-slate-100" : "hover:bg-slate-50"].join(" ")}
                  onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{s.name}</p>
                    <p className="text-xs text-slate-500">{fmt(s.value)} &middot; {s.pct.toFixed(1)} %</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* RECHERCHE */}
      <div className="mb-4 flex gap-2">
        <input type="search"
          placeholder={t("suppliersPage.searchPlaceholder")}
          value={q} onChange={e => setQ(e.target.value)}
          className="input flex-1" />
      </div>

      {/* LISTE */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {error && <p role="alert" className="p-4 text-sm text-red-600">{error}</p>}
        {loading && <p className="p-6 text-sm text-slate-500">Chargement…</p>}
        {!loading && filtered.length === 0 && !error && (
          <p className="p-6 text-sm text-slate-500">
            {t("suppliersPage.noInvoices")}
          </p>
        )}
        {!loading && filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("suppliersPage.colRef")}</th>
                <th className="px-4 py-2 font-medium">{t("suppliersPage.supplier")}</th>
                <th className="px-4 py-2 font-medium">{t("suppliersPage.colDate")}</th>
                <th className="px-4 py-2 font-medium">{t("suppliersPage.colDue")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("suppliersPage.colTtc")}</th>
                <th className="px-4 py-2 font-medium text-center">{t("suppliersPage.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">{inv.reference as string}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {clients.find(c => c.id === inv.client_id)?.name ?? "—"}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{inv.issue_date as string}</td>
                  <td className="px-4 py-2 text-slate-600">{(inv.due_date as string) ?? "—"}</td>
                  <td className="px-4 py-2 text-right font-medium text-slate-900">
                    {fmt(parseFloat(inv.amount_ttc as unknown as string) || 0)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}
